import { useCallback, useState } from 'react';
import { useQuery, useLazyQuery } from '@apollo/client';
import { useNavigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import {
  Typography,
  Stack,
  Box,
  CircularProgress,
  Link,
  styled,
} from '@mui/material';
import { theme } from '~/shared/styles/theme';
import { ROUTES } from '~/shared/constants';
import { PrimaryButton, OnboardingLayout } from '../components';
import { handleLogout as handleLogoutUtil } from '~/shared/utils/auth';
import {
  GET_ONBOARDING_STATUS,
  GET_MDI_ONBOARDING_URL,
} from '~/graphql/queries';
import useToast from '~/shared/hooks/useToast';
import MdiOnboardingModal from '~/shared/components/MdiOnboardingModal';
import Toast from '~/shared/components/Toast';
import { RiskTier } from '~/graphql/gen/graphql';
import { DNA } from '~/assets/icons';

interface OnboardingStatus {
  doesUserExist: boolean;
  isConsentAcknowledged: boolean;
  isCompleted: boolean;
  riskTier: RiskTier | null;
  hasDeclinedInvitation: boolean;
}

export const MdiExpectationsScreen = () => {
  const navigate = useNavigate();
  const { logout } = useAuth0();

  const { data, loading, error } = useQuery(GET_ONBOARDING_STATUS);
  const status: OnboardingStatus = {
    doesUserExist: data?.onboardingStatus?.userExists ?? false,
    isConsentAcknowledged: data?.onboardingStatus?.consentAcknowledged ?? false,
    isCompleted: data?.onboardingStatus?.completed ?? false,
    riskTier: data?.onboardingStatus?.riskTier ?? null,
    hasDeclinedInvitation:
      data?.onboardingStatus?.hasDeclinedInvitation ?? false,
  };

  const [isMdiModalOpen, setIsMdiModalOpen] = useState(false);
  const [mdiUrl, setMdiUrl] = useState<string | null>(null);
  const { toast, showToast, hideToast } = useToast();
  const [getMdiOnboardingUrl] = useLazyQuery(GET_MDI_ONBOARDING_URL);

  const handleConnectWithDoctor = useCallback(async () => {
    setIsMdiModalOpen(true);
    setMdiUrl(null);

    try {
      const { data, error } = await getMdiOnboardingUrl();

      if (error || !data?.getMdiOnboardingUrl) {
        showToast(
          'Failed to get MDI onboarding URL. Please try again.',
          'error',
        );
        setIsMdiModalOpen(false);
        return;
      }

      setMdiUrl(data.getMdiOnboardingUrl);
    } catch (err) {
      showToast('Failed to request MDI onboarding. Please try again.', 'error');
      setIsMdiModalOpen(false);
    }
  }, [getMdiOnboardingUrl, showToast]);

  const handleBuildFamilyTree = () => {
    navigate(ROUTES.ONBOARDING.ROOT);
  };

  const handleMdiModalClose = useCallback(() => {
    setIsMdiModalOpen(false);
    setMdiUrl(null);

    // If user declined invitation, keep them on results screen
    // Otherwise, navigate to onboarding form
    if (status.hasDeclinedInvitation) {
      navigate(ROUTES.ONBOARDING.RESULTS);
    } else {
      navigate(ROUTES.ONBOARDING.ROOT);
    }
  }, [navigate, status.hasDeclinedInvitation]);

  const handleLogout = useCallback(() => {
    handleLogoutUtil(logout);
  }, [logout]);

  if (loading) {
    return (
      <OnboardingLayout showLogOut={true} onLogOut={handleLogout}>
        <Stack justifyContent="center" alignItems="center" flex={1}>
          <CircularProgress />
        </Stack>
      </OnboardingLayout>
    );
  }

  if (error) {
    return (
      <OnboardingLayout showLogOut={true} onLogOut={handleLogout}>
        <Stack spacing={3} alignItems="center">
          <Typography variant="h5" color="error" textAlign="center">
            Unable to Load Page
          </Typography>
          <Typography color="text.secondary" textAlign="center">
            We encountered an error while loading this page. Please try
            refreshing the page or contact support if the problem persists.
          </Typography>
          <PrimaryButton
            variant="green"
            onClick={() => window.location.reload()}
          >
            Refresh Page
          </PrimaryButton>
        </Stack>
      </OnboardingLayout>
    );
  }

  return (
    <OnboardingLayout showLogOut={true} onLogOut={handleLogout}>
      <Stack
        direction="row"
        spacing={9}
        alignItems="center"
        mb={8}
        mt={8}
        mx={22}
      >
        <IconContainer>
          <DNA width="51" height="51" />
        </IconContainer>

        <HeadlineText>Get Tested for OTC Deficiency</HeadlineText>
      </Stack>

      <WhatToExpectSection mb={16} mx={12}>
        <SectionTitle>What to Expect</SectionTitle>

        <Stack spacing={6} mt={8} mx={2}>
          <ExpectationItem>
            <NumberBadge>1</NumberBadge>
            <ExpectationText>
              You'll be redirected to MDI, our trusted testing partner
            </ExpectationText>
          </ExpectationItem>

          <ExpectationItem>
            <NumberBadge>2</NumberBadge>
            <ExpectationText>
              MDI and LabCorp will guide you through their consent process
            </ExpectationText>
          </ExpectationItem>

          <ExpectationItem>
            <NumberBadge>3</NumberBadge>
            <ExpectationText>Complete your genetic test</ExpectationText>
          </ExpectationItem>

          <ExpectationItem>
            <NumberBadge>4</NumberBadge>
            <ExpectationText>
              Return here to create your family tree and invite family members
              to get tested
            </ExpectationText>
          </ExpectationItem>
        </Stack>
      </WhatToExpectSection>

      <Stack spacing={7}>
        <PrimaryButton variant="brown" onClick={handleConnectWithDoctor}>
          Connect with a Doctor
        </PrimaryButton>

        {!status.hasDeclinedInvitation && (
          <SecondaryLink onClick={handleBuildFamilyTree}>
            Skip & Build Your Family Tree
          </SecondaryLink>
        )}
      </Stack>

      <MdiOnboardingModal
        open={isMdiModalOpen}
        onClose={handleMdiModalClose}
        url={mdiUrl}
      />

      <Toast
        open={toast.open}
        message={toast.message}
        severity={toast.severity}
        onClose={hideToast}
      />
    </OnboardingLayout>
  );
};

const IconContainer = styled(Box)({});

const HeadlineText = styled(Typography)({
  fontSize: '30px',
  fontWeight: 500,
  color: theme.palette.custom.textTitle,
});

const WhatToExpectSection = styled(Stack)({});

const SectionTitle = styled(Typography)({
  fontSize: '20px',
  fontWeight: 700,
  color: theme.palette.custom.textSupporting,
  textAlign: 'left',
});

const ExpectationItem = styled(Stack)({
  flexDirection: 'row',
  alignItems: 'flex-start',
  gap: theme.spacing(3),
});

const NumberBadge = styled(Stack)({
  display: 'flex',
  width: '32px',
  height: '32px',
  borderRadius: '50%',
  backgroundColor: theme.palette.custom.limeGreen,
  color: theme.palette.custom.textTitle,
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: '16px',
  fontWeight: 600,
  lineHeight: 1,
  flexShrink: 0,
});

const ExpectationText = styled(Typography)({
  fontSize: '18px',
  fontWeight: 500,
  color: theme.palette.custom.textSupporting,
  lineHeight: '25px',
  flex: 1,
});

const SecondaryLink = styled(Link)({
  color: theme.palette.custom.darkBrown,
  textDecoration: 'underline',
  cursor: 'pointer',
  fontSize: '16px',
  fontWeight: 500,
  textAlign: 'center',
});
